<template>
  <div class="min-h-screen bg-background font-sans selection:bg-primary/10">
    <main class="pt-32 pb-20 container mx-auto px-4">
      <!-- Hero Section -->
      <transition name="fade-up" appear>
        <div class="text-center mb-20">
          <h1 class="text-4xl md:text-6xl font-serif text-primary mb-6">Workshops & Contact</h1>
          <p class="max-w-2xl mx-auto text-muted-foreground">
            Join our creative community to learn, share, and grow.
            Book a session or get in touch for collaborations.
          </p>
        </div>
      </transition>

      <!-- Workshops Grid -->
      <section class="mb-24">
        <h2 class="text-3xl font-serif mb-8 text-center md:text-left">Upcoming Workshops</h2>
        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <transition-group name="list" appear>
            <div v-for="(workshop, index) in workshops" :key="workshop.id"
              :style="{ transitionDelay: `${index * 0.1}s` }">
              <div
                class="h-full border-border/50 overflow-hidden group hover:shadow-md transition-all duration-300 rounded-lg">
                <div class="h-48 overflow-hidden relative">
                  <img :src="workshop.image" :alt="workshop.title"
                    class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div
                    class="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 text-sm font-medium rounded-full shadow-sm">
                    ${{ workshop.price }}
                  </div>
                </div>
                <div class="p-6">
                  <h3 class="font-serif text-xl mb-2">{{ workshop.title }}</h3>
                  <p class="text-muted-foreground text-sm leading-relaxed mb-4">
                    {{ workshop.description }}
                  </p>
                  <div class="space-y-2 text-sm">
                    <div class="flex items-center gap-2 text-muted-foreground">
                      <CalendarIcon :size="16" />
                      <span>{{ workshop.date }}</span>
                    </div>
                    <div class="flex items-center gap-2 text-muted-foreground">
                      <ClockIcon :size="16" />
                      <span>{{ workshop.time }}</span>
                    </div>
                    <div class="flex items-center gap-2 text-muted-foreground">
                      <MapPinIcon :size="16" />
                      <span>{{ workshop.location }}</span>
                    </div>
                  </div>
                </div>
                <div class="p-6 pt-0">
                  <button @click="openBookingDialog(workshop)"
                    class="w-full bg-primary text-white py-2 rounded-md hover:bg-primary/90 transition-colors">
                    Book Now
                  </button>
                </div>
              </div>
            </div>
          </transition-group>
        </div>
      </section>

      <!-- Contact Section -->
      <section class="grid md:grid-cols-2 gap-12 lg:gap-24 items-start">
        <!-- Contact Info -->
        <div class="space-y-8">
          <div>
            <h2 class="text-3xl font-serif mb-6">Get in Touch</h2>
            <p class="text-muted-foreground leading-relaxed">
              Have questions about commissions, shipping, or upcoming events?
              Feel free to reach out. I'm always open to discussing new projects and creative ideas.
            </p>
          </div>

          <div class="space-y-6">
            <div class="flex items-start gap-4">
              <div class="bg-secondary/50 p-3 rounded-full">
                <MailIcon :size="24" class="text-primary" />
              </div>
              <div>
                <h3 class="font-medium mb-1">Email</h3>
                <a href="mailto:artpulsebyshruti@gmail.com"
                  class="text-muted-foreground hover:text-primary transition-colors">
                  artpulsebyshruti@gmail.com
                </a>
              </div>
            </div>

            <div class="flex items-start gap-4">
              <div class="bg-secondary/50 p-3 rounded-full">
                <PhoneIcon :size="24" class="text-primary" />
              </div>
              <div>
                <h3 class="font-medium mb-1">Phone</h3>
                <p class="text-muted-foreground">+91 98765 43210</p>
              </div>
            </div>

            <div class="flex items-start gap-4">
              <div class="bg-secondary/50 p-3 rounded-full">
                <MapPinIcon :size="24" class="text-primary" />
              </div>
              <div>
                <h3 class="font-medium mb-1">Studio</h3>
                <p class="text-muted-foreground">
                  123 Art Avenue, Colaba<br />
                  Mumbai, Maharashtra 400005
                </p>
              </div>
            </div>
          </div>

          <div class="pt-6 border-t border-border">
            <h3 class="font-medium mb-4">Follow ArtPulse</h3>
            <div class="flex gap-4">
              <a href="https://www.instagram.com/artpulse_by_shruti/" target="_blank" class="inline-flex items-center justify-center rounded-full 
             hover:bg-primary hover:text-black transition-colors 
             p-2 border border-gray-300">
                <i class="fa-brands fa-instagram fs-24"></i>
              </a>
            </div>
          </div>
        </div>

        <!-- Contact Form -->
        <div class="border-border/50 shadow-sm rounded-lg p-6 bg-white">
          <h2 class="font-serif text-2xl mb-4">Send a Message</h2>
          <form @submit.prevent="handleContactSubmit" class="space-y-4">
            <div class="grid gap-2">
              <label for="contact-name" class="font-medium">Name</label>
              <input id="contact-name" v-model="contactForm.name" required
                class="border p-2 rounded-md w-full border-black" />
            </div>
            <div class="grid gap-2">
              <label for="contact-email" class="font-medium">Email</label>
              <input id="contact-email" type="email" v-model="contactForm.email" required
                class="border p-2 rounded-md w-full border-black" />
            </div>
            <div class="grid gap-2">
              <label for="contact-message" class="font-medium">Message</label>
              <textarea id="contact-message" class="min-h-[150px] border p-2 rounded-md w-full border-black"
                v-model="contactForm.message" required></textarea>
            </div>
            <button type="submit" class="w-full bg-primary text-white py-3 rounded-md disabled:opacity-50"
              :disabled="isSending">
              {{ isSending ? "Sending..." : "Send Message" }}
            </button>
          </form>
        </div>
      </section>
    </main>

    <!-- Booking Dialog Modal -->
    <transition name="fade">
      <div v-if="showBookingDialog" @click.self="closeBookingDialog"
        class="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
        <transition name="zoom">
          <div v-if="showBookingDialog"
            class="relative bg-white rounded-lg shadow-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-xl font-serif">Book Workshop</h3>
              <button @click="closeBookingDialog" class="p-2">
                <!-- <XIcon class="h-5 w-5" /> -->
              </button>
            </div>
            <p class="text-muted-foreground text-sm mb-4">
              {{ selectedWorkshop.title }} • {{ selectedWorkshop.date }}
            </p>
            <form @submit.prevent="handleBookingSubmit" class="space-y-4">
              <div class="grid gap-2">
                <label for="booking-name" class="font-medium">Name</label>
                <input id="booking-name" v-model="bookingForm.name" required
                  class="border p-2 rounded-md w-full border-black" />
              </div>
              <div class="grid gap-2">
                <label for="booking-email" class="font-medium">Email</label>
                <input id="booking-email" type="email" v-model="bookingForm.email" required
                  class="border p-2 rounded-md w-full border-black" />
              </div>
              <div class="grid gap-2">
                <label for="booking-phone" class="font-medium">Phone</label>
                <input id="booking-phone" type="tel" v-model="bookingForm.phone" required
                  class="border p-2 rounded-md w-full border-black" />
              </div>
              <button type="submit" class="w-full bg-primary text-white py-3 rounded-md disabled:opacity-50"
                :disabled="isBookingSubmitting">
                {{ isBookingSubmitting ? "Processing..." : `Confirm Booking ($${selectedWorkshop.price})` }}
              </button>
            </form>
          </div>
        </transition>
      </div>
    </transition>

    <!-- Simple Toast Notification -->
    <transition name="fade">
      <div v-if="toast.visible" class="fixed top-5 right-5 bg-green-500 text-white p-4 rounded-lg shadow-lg z-50">
        <p class="font-bold">{{ toast.title }}</p>
        <p>{{ toast.description }}</p>
      </div>
    </transition>
  </div>
</template>

<script>
import DewDrops from '../../assets/img/red_rose.jpg'
import VintageSoul from '../../assets/img/kishore_da_potrait.jpeg'
import OpenFields from '../../assets/img/scenry.jpg'

const WORKSHOPS = [
  {
    id: 1,
    title: "Watercolor Florals for Beginners",
    date: "Dec 15, 2025",
    time: "10:00 AM - 1:00 PM",
    price: 45,
    image: DewDrops,
    description: "Learn the delicate art of watercolor flowers. We will cover color blending, brush techniques, and composition.",
    location: "ArtPulse Studio, Mumbai",
    spots: 5
  },
  {
    id: 2,
    title: "Mastering Charcoal Portraits",
    date: "Dec 22, 2025",
    time: "2:00 PM - 5:00 PM",
    price: 60,
    image: VintageSoul,
    description: "Dive into the world of shadows and light. Learn how to capture expressions and depth using charcoal.",
    location: "Online (Zoom)",
    spots: 10
  },
  {
    id: 3,
    title: "Landscape Painting Workshop",
    date: "Jan 05, 2026",
    time: "11:00 AM - 3:00 PM",
    price: 75,
    image: OpenFields,
    description: "A comprehensive session on capturing nature's beauty. Suitable for acrylic and oil mediums.",
    location: "ArtPulse Studio, Mumbai",
    spots: 8
  }
];

export default {
  name: 'WorkshopsPage',
  data() {
    return {
      workshops: WORKSHOPS,
      contactForm: {
        name: '',
        email: '',
        message: '',
      },
      isSending: false,
      showBookingDialog: false,
      selectedWorkshop: null,
      bookingForm: {
        name: '',
        email: '',
        phone: '',
      },
      isBookingSubmitting: false,
      toast: {
        visible: false,
        title: '',
        description: '',
      },
    };
  },
  methods: {
    handleContactSubmit() {
      this.isSending = true;
      setTimeout(() => {
        this.isSending = false;
        this.contactForm = { name: '', email: '', message: '' };
        this.showToast("Message Sent", "Thank you for reaching out! We'll get back to you soon.");
      }, 1500);
    },
    openBookingDialog(workshop) {
      this.selectedWorkshop = workshop;
      this.showBookingDialog = true;
      document.body.style.overflow = 'hidden'; // Prevent scrolling when modal is open
    },
    closeBookingDialog() {
      this.selectedWorkshop = null;
      this.showBookingDialog = false;
      this.bookingForm = { name: '', email: '', phone: '' }; // Reset form
      document.body.style.overflow = ''; // Restore scrolling
    },
    handleBookingSubmit() {
      this.isBookingSubmitting = true;
      setTimeout(() => {
        this.isBookingSubmitting = false;
        this.showBookingDialog = false;
        this.showToast(
          "Booking Request Sent",
          `We've reserved your spot for "${this.selectedWorkshop.title}". Check your email for payment details.`
        );
        this.bookingForm = { name: '', email: '', phone: '' }; // Reset form
      }, 1500);
    },
    showToast(title, description) {
      this.toast.title = title;
      this.toast.description = description;
      this.toast.visible = true;
      setTimeout(() => {
        this.toast.visible = false;
      }, 5000);
    },
  },
  beforeUnmount() {
    // Ensure body scrolling is restored if component is unmounted while modal is open
    document.body.style.overflow = '';
  }
};
</script>

<style scoped>
.fade-up-enter-active,
.fade-up-leave-active {
  transition: opacity 0.8s, transform 0.8s;
}

.fade-up-enter-from,
.fade-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.list-leave-active {
  position: absolute;
  /* For smooth removal of items */
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.zoom-enter-active,
.zoom-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.zoom-enter-from,
.zoom-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>